import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import n8nService from '../services/n8nService';

interface ManualTaskFormProps {
  open: boolean;
  onClose: () => void;
  onTaskCreated: (task: any) => void;
}

interface TaskFormData {
  title: string;
  description: string;
  link: string;
  quadrant: string;
  status: string;
}

const ManualTaskForm: React.FC<ManualTaskFormProps> = ({
  open,
  onClose,
  onTaskCreated,
}) => {
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    link: '',
    quadrant: 'urgent-important',
    status: 'pending',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quadrantOptions = [
    { value: 'urgent-important', label: 'Urgent & Important (Do First)' },
    { value: 'important', label: 'Important (Schedule)' },
    { value: 'urgent', label: 'Urgent (Delegate)' },
    { value: 'neither', label: 'Neither (Eliminate)' },
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
  ];

  const handleInputChange = (field: keyof TaskFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newTask = await n8nService.createTask({
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        link: formData.link.trim() || undefined,
        quadrant: formData.quadrant as any,
        status: formData.status as any,
        archived: false, // New tasks are never archived initially
      });

      // Reset form
      setFormData({
        title: '',
        description: '',
        link: '',
        quadrant: 'urgent-important',
        status: 'pending',
      });

      // Notify parent component
      onTaskCreated(newTask);
      
      // Close dialog
      onClose();
      
    } catch (error) {
      console.error('Error creating task:', error);
      setError(error instanceof Error ? error.message : 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        title: '',
        description: '',
        link: '',
        quadrant: 'urgent-important',
        status: 'pending',
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2 },
          m: { xs: 0, sm: 2 },
          // Make dialog fullscreen on mobile
          '@media (max-width: 599px)': {
            height: '100%',
            maxHeight: '100%',
            margin: 0,
            borderRadius: 0
          }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon color="primary" />
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            Create New Task
          </Typography>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1, px: { xs: 2, sm: 3 } }}>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {error}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={{ xs: 1.5, sm: 2 }}>
            <TextField
              label="Task Title"
              value={formData.title}
              onChange={handleInputChange('title')}
              required
              fullWidth
              placeholder="e.g., Complete project proposal"
              disabled={loading}
              autoFocus
            />

            <TextField
              label="Description (Optional)"
              value={formData.description}
              onChange={handleInputChange('description')}
              multiline
              rows={3}
              fullWidth
              placeholder="Add more details about this task..."
              disabled={loading}
            />

            <TextField
              label="Link (Optional)"
              value={formData.link}
              onChange={handleInputChange('link')}
              fullWidth
              placeholder="e.g., https://example.com"
              disabled={loading}
              type="url"
            />

            <FormControl fullWidth disabled={loading}>
              <InputLabel>Quadrant</InputLabel>
              <Select
                value={formData.quadrant}
                onChange={handleInputChange('quadrant')}
                label="Quadrant"
              >
                {quadrantOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={loading}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleInputChange('status')}
                label="Status"
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            color="inherit"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.title.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            sx={{
              minWidth: { xs: 100, sm: 120 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 }
            }}
          >
            {loading ? 'Creating...' : 'Create Task'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManualTaskForm;
